"use client";
import { CANDLE_COLOR, CHART_COLORS } from "@/app/theme/theme";
import { getImageUrl } from "@/lib/getImageUrl";
import { selectLogBase } from "@/lib/logBase";
import { PerformanceProofMetric } from "@/lib/PerformanceProofSource";
import { getCryptoLongProfit } from "@/lib/cryptoData/bitcoin";
import { Box, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { BuySellType, TrendType } from "@prisma/client";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import EChartsBase from "./EChartsBase";
import { LoadingSmall } from "./Loading";

dayjs.extend(isBetween);

interface ChartProps {
  perfProof: PerformanceProofMetric;
  chartHeight?: string;
  symbol?: "btc" | "eth" | "sol";
}

const ZoneBear = "rgba(224, 83, 87, 0.10)";
const ZoneBull = "rgba(19, 158, 121, 0.10)";
const ZoneBearBorder = "#E05357";
const ZoneBullBorder = "#139E79";

export default function PerformanceProofChart({ perfProof, chartHeight = "18.5rem", symbol = "btc" }: ChartProps) {
  const theme = useTheme();
  const [option, setOption] = useState<any>();
  const [data, setData] = useState<any>();
  const [longProfit, setLongProfit] = useState<number | null>(null);

  const t = useTranslations();

  useEffect(() => {
    loadData();
    fetchLongProfit();
  }, []);

  async function fetchLongProfit() {
    try {
      const profit = await getCryptoLongProfit(symbol);
      setLongProfit(profit?.value ?? null);
    } catch (e) {
      setLongProfit(null);
    }
  }

  async function loadData() {
    try {
      const data = await perfProof.getData();
      setData(data);

      // 买卖点标记数据
      const markPoints = data.buySellPoints
        .map((signal) => {
          const formattedDate = dayjs(signal.date).format("YYYY-MM-DD");
          const xIndex = data.dates.indexOf(formattedDate);
          return xIndex >= 0
            ? {
                name: signal.type,
                coord: [xIndex, data.values[xIndex]],
                symbol: `image:///images/${signal.type === BuySellType.BUY ? "buy" : "sell"}mark.png`,
                symbolOffset: [0, signal.type === BuySellType.BUY ? 12 : -12],
                symbolSize: [12, 16],
              }
            : null;
        })
        .filter(Boolean);

      // 获取有效日期范围
      const minDate = dayjs(data.dates[0]);
      const maxDate = dayjs(data.dates[data.dates.length - 1]);

      // 趋势区间
      const markAreas = [];
      const markLines = [];

      for (const zone of data.trendZones) {
        // 限制区间在有效范围内
        const zoneStart = dayjs(zone.startDate);
        const zoneEnd = dayjs(zone.endDate);
        const areaStart = zoneStart.isAfter(minDate) ? zoneStart : minDate;
        const areaEnd = zoneEnd.isBefore(maxDate) ? zoneEnd : maxDate;

        // 只有区间有交集且起止有效才展示
        markAreas.push([
          {
            xAxis: areaStart.format("YYYY-MM-DD"),
            itemStyle: {
              color: zone.trend === TrendType.BEAR ? ZoneBear : ZoneBull,
            },
          },
          {
            xAxis: areaEnd.format("YYYY-MM-DD"),
          },
        ]);
        if (zoneStart.isAfter(minDate)) {
          markLines.push({
            xAxis: zoneStart.format("YYYY-MM-DD"),
            lineStyle: {
              type: "dotted",
              color: zone.trend === TrendType.BEAR ? ZoneBearBorder : ZoneBullBorder,
              width: 2,
            },
            label: { show: false },
          });
        }
        if (zoneEnd.isBefore(maxDate)) {
          markLines.push({
            xAxis: zoneEnd.format("YYYY-MM-DD"),
            lineStyle: {
              type: "dotted",
              color: zone.trend === TrendType.BEAR ? ZoneBearBorder : ZoneBullBorder,
              width: 2,
            },
            label: { show: false },
          });
        }
      }

      // 计算数据点的最小值和最大值，用于确定最佳对数底数
      const minValue = Math.min(...data.values.filter((v) => v > 0)); // 过滤掉非正数
      const maxValue = Math.max(...data.values);

      // 使用selectLogBase函数确定最佳对数底数
      const optimalLogBase = selectLogBase(minValue, maxValue);

      const option = {
        grid: {
          left: "50px",
          right: "20px",
        },
        tooltip: {
          trigger: "axis",
        },
        xAxis: {
          type: "category",
          data: data.dates,
        },
        yAxis: {
          type: "log", // 将线性刻度改为对数刻度
          logBase: optimalLogBase, // 使用计算出的最佳底数
          axisLabel: {
            formatter: (value: number) => {
              return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
            },
          },
        },
        series: [
          {
            type: "line",
            data: data.values,
            symbol: "none", // 不显示圆点
            markPoint: {
              symbolSize: [12, 16],
              emphasis: { scale: 1.5 },
              data: markPoints,
            },
            markArea: {
              data: markAreas,
            },
            markLine: {
              symbol: "none",
              data: markLines,
            },
          },
        ],
        graphic: {
          id: "watermark",
          type: "image",
          right: 20,
          bottom: 70,
          style: {
            image: getImageUrl("watermark.png", theme.palette.mode),
            width: 148,
            height: 37,
          },
        },
      };

      setOption(option);
    } catch (error) {
      console.error("Failed to load chart data:", error);
    }
  }

  function renderLongProfit(val: number | null) {
    if (val === null) return "+ --%";
    const sign = val > 0 ? "+" : val < 0 ? "−" : "";
    return `${sign} ${Math.abs(val)}%`;
  }

  return (
    <Box
      sx={{
        py: 1.5,
        px: 2,
        backgroundColor: "background.paperMedium",
        borderRadius: 3,
        width: "100%",
        position: "relative",
      }}>
      <div>
        <Typography
          variant="h5"
          sx={{
            fontSize: "var(--font-size-s)",
            fontWeight: 600,
            minWidth: 0,
          }}>
          {t("Metrics.proof.title")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            pb: 2,
          }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "var(--font-size-2xs)",
              color: "text.tertiary",
            }}>
            <Typography component="span" sx={{ fontSize: "var(--font-size-xs)", color: "text.secondary" }}>
              {t("Metrics.proof.profit", { crypto: t(`Crypto.${symbol}.abbr`) })}
              <Typography component="span" sx={{ color: CANDLE_COLOR.BULLISH, ml: 1 }}>
                {renderLongProfit(longProfit)}
              </Typography>
            </Typography>
          </Typography>
          {data && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "var(--font-size-2xs)",
                color: "text.tertiary",
              }}>
              {t.rich("Metrics.proof.update", { date: dayjs().format("YYYY/MM/DD") })}
            </Typography>
          )}
        </Box>
        {option ? (
          <Box sx={{ position: "relative" }}>
            <EChartsBase option={option} theme={theme.palette.mode} style={{ height: chartHeight, width: "100%" }} />
            {/* 自定义左下角 legends */}
            <Box
              sx={{
                position: "absolute",
                left: "1.5rem",
                bottom: "1.125rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
                zIndex: 2,
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: "0.75rem",
                    height: "0.75rem",
                    borderRadius: "50%",
                    bgcolor: CHART_COLORS[0],
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {data && t(data.title)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: "0.75rem",
                    height: "0.75rem",
                    borderRadius: "50%",
                    bgcolor: CANDLE_COLOR.BULLISH,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {symbol === "btc" ? t("Metrics.proof.buyZone") : t("Metrics.proof.uptrend")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: "0.75rem",
                    height: "0.75rem",
                    borderRadius: "50%",
                    bgcolor: CANDLE_COLOR.BEARISH,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {symbol === "btc" ? t("Metrics.proof.sellZone") : t("Metrics.proof.downtrend")}
                </Typography>
              </Box>
            </Box>
            {/* 当 data.hasSub 为 false 时在图表右侧显示遮罩，阻止交互 */}
            {data && data.hasSub === false && (
              <Box
                sx={(theme) => ({
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: { xs: "12rem", sm: "12rem", md: "21.5rem" },
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "auto",
                  // use paperMedium for theme-aware mask
                  background: `linear-gradient(to left, ${alpha(
                    (theme as any).palette.background.paperMedium,
                    1
                  )}, ${alpha((theme as any).palette.background.paperMedium, 0)})`,
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                })}>
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: "16rem",
                    px: 1,
                    fontSize: "var(--font-size-xs)",
                    color: (theme as any).palette.text.primary,
                    textAlign: "center",
                  }}>
                  {symbol === "btc"
                    ? (t as any)("Metrics.proof.mask.trackMarket")
                    : (t as any)("Metrics.proof.mask.trackTrend")}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <LoadingSmall sx={{ height: `calc(${chartHeight} - 2rem)` }} />
        )}
      </div>
    </Box>
  );
}
